import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { togglePrimitiveItemInArray } from 'utils/helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageData } from 'types/Page';
import { Button, Field, TextInput } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import { unstable_BlockerFunction as BlockerFunction } from 'react-router-dom';
import { UseMutateAsyncFunction } from 'react-query';
import UnsavedChangesHandler, {
  useUnsavedChangesHandler,
} from 'app/components/UnsavedChangesHandler';
import ContentPlanApi from 'app/api/contentPlan';
import { z } from 'zod';
import dayjs from 'dayjs';
import { pageDataToProfilePartial } from 'app/modules/posts/utils/pageDataToProfilePartial';
import ProfilesSelector from 'app/components/ProfilesSelector';
import RangeDatePicker from 'app/components/datepicker-dropdown/RangeDatePicker';
import { useTranslation } from 'react-i18next';

const dateSchema = z.date({
  required_error: 'enter date',
  invalid_type_error: 'must be valid date range',
});

const schema = z.object({
  name: z.string().min(1, 'enter name'),
  startDate: dateSchema,
  endDate: dateSchema,
  profileIds: z
    .array(z.number())
    .min(1, { message: 'at least one profile must be selected' }),
});

type ValidationSchema = z.infer<typeof schema>;

type FormState = MarkNullable<ValidationSchema, 'startDate' | 'endDate'>;

type Props = {
  profiles: PageData[];
  initialValue?: FormState;
  onSubmit: UseMutateAsyncFunction<
    Awaited<ReturnType<typeof ContentPlanApi.create>>,
    unknown,
    Parameters<typeof ContentPlanApi.create>[0],
    unknown
  >;
  isLoading: boolean;
};

const defaultValues = {
  name: '',
  startDate: null,
  endDate: null,
  profileIds: [],
};

const Form: FC<Props> = ({ onSubmit, initialValue, profiles, isLoading }) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
    control,
  } = useForm<FormState, any, ValidationSchema>({
    defaultValues: {
      ...defaultValues,
      ...initialValue,
    },
    resolver: zodResolver(schema),
  });
  const { t } = useTranslation();
  const disableUnsavedChangesHandler = useUnsavedChangesHandler();
  const [startDate, endDate] = watch(['startDate', 'endDate']);

  register('startDate');
  register('endDate');

  const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) => {
    return currentLocation.hash !== nextLocation.hash;
  };

  return (
    <UnsavedChangesHandler
      type="contentPlan"
      variant="create"
      shouldBlock={shouldBlock}
      isSubmitting={isLoading}
      onSubmit={(options) => {
        void handleSubmit(
          (values) => onSubmit(values, options),
          options.onError,
        )();
      }}
    >
      <form
        onSubmit={handleSubmit((values) =>
          disableUnsavedChangesHandler(() => onSubmit(values)),
        )}
      >
        <Modal.Content className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-gap-4">
            <Field.Group className="tw-w-full tw-flex-1">
              <Field.Label required>{t('contentPlanName')}</Field.Label>
              <TextInput
                placeholder={t('contentPlanNamePlaceholder')}
                {...register('name')}
              />
              <Field.Error>{errors.name?.message}</Field.Error>
            </Field.Group>
            <Field.Group className="tw-w-full tw-flex-1">
              <Field.Label required>{t('dateRange')}</Field.Label>
              <RangeDatePicker
                startDate={startDate ? dayjs(startDate) : null}
                endDate={endDate ? dayjs(endDate) : null}
                onChange={({ startDate, endDate }) => {
                  setValue('startDate', startDate?.toDate() ?? null);
                  setValue('endDate', endDate?.toDate() ?? null);
                }}
                applyOnChange
              />
              <Field.Error>
                {errors.startDate?.message || errors.endDate?.message}
              </Field.Error>
            </Field.Group>
          </div>
          <Field.Group>
            <Controller
              control={control}
              name="profileIds"
              render={({ field: { value, onChange } }) => (
                <ProfilesSelector
                  className={{
                    list: 'tw-max-h-[55vh] tw-overflow-y-auto',
                  }}
                  profiles={profiles.map(pageDataToProfilePartial)}
                  selectedIds={value}
                  onSelect={(id) =>
                    onChange(togglePrimitiveItemInArray(id, value))
                  }
                  onToggleAllSelection={(selection) =>
                    selection === 'all'
                      ? onChange(profiles.map((profile) => profile.id))
                      : onChange([])
                  }
                />
              )}
            />
            <Field.Error>{errors.profileIds?.message}</Field.Error>
          </Field.Group>
        </Modal.Content>
        <Modal.Footer>
          <Button variant="primary" isLoading={isLoading} type="submit">
            {t('create')}
          </Button>
        </Modal.Footer>
      </form>
    </UnsavedChangesHandler>
  );
};

export default Form;
